import React from 'react'

import NiceModal from '@ebay/nice-modal-react'
import { Divider, Group, Radio, ScrollArea, Stack, Text } from '@mantine/core'

import { ScriptDocType } from '@showrunner/codex'

import { ModalShell, useModalControls } from '#components/Modals'
import { sleneTypeForDoc } from '#util'

import { SleneItem } from '../helpers'

import styles from './SelectSlenesModal.module.scss'

const FULL_DOCUMENT = 'full'

// See (../../Modals/readme.md)
// oxlint-disable-next-line react/only-export-components
const SideSelector = ({
  position,
  items,
  selectedId,
  setSelectedId,
}: {
  position: LeftRight
  items: Array<SleneItem>
  selectedId: string
  setSelectedId: (id: string) => void
}) => {
  return (
    <Stack className={styles.sideSelector} gap={0}>
      <div className={styles.sideTitle}>
        {position === 'left' ? 'Snapshot' : 'Current Document'}
      </div>

      <Radio
        checked={selectedId === 'full'}
        value="full"
        label="Full Document"
        className={styles.fullDocument}
        onChange={() => setSelectedId('full')}
      />
      <Divider orientation="horizontal" className={styles.divider} />
      <ScrollArea className={styles.sleneList_scroller} type="auto">
        <Stack className={styles.sleneList}>
          {items.map(({ id, label }) => (
            <Radio
              className={styles.sideRadio}
              key={id}
              label={label}
              value={id}
              checked={selectedId === id}
              onChange={() => setSelectedId(id)}
            />
          ))}
        </Stack>
      </ScrollArea>
    </Stack>
  )
}

export type SelectSlenesProps = {
  leftItems: SleneItem[]
  rightItems: SleneItem[]
  selectedLeftId?: string | null
  selectedRightId?: string | null
  docType: ScriptDocType
  onSelect: (choices: {
    left: SleneItem | null
    right: SleneItem | null
  }) => void
}

const SelectSlenesModal = NiceModal.create<SelectSlenesProps>(
  ({
    leftItems,
    rightItems,
    selectedLeftId,
    selectedRightId,
    docType,
    onSelect,
  }) => {
    const title = `Select ${sleneTypeForDoc(docType)}s to compare`
    const { onClose, opened } = useModalControls()
    const [localLeftId, setLocalLeftId] = React.useState<string>(
      selectedLeftId ?? FULL_DOCUMENT,
    )
    const [localRightId, setLocalRightId] = React.useState<string>(
      selectedRightId ?? FULL_DOCUMENT,
    )

    const handleSelect = () => {
      const left = leftItems.find((item) => item.id === localLeftId) ?? null
      const right = rightItems.find((item) => item.id === localRightId) ?? null
      onSelect({ left, right })
      onClose()
    }

    return (
      <ModalShell
        size={600}
        confirmLabel="Compare"
        cancelLabel="Cancel"
        opened={opened}
        title={title}
        onClose={onClose}
        onConfirm={handleSelect}
      >
        <Group gap={10} justify="center" align="flex-start">
          <SideSelector
            position="left"
            selectedId={localLeftId}
            setSelectedId={setLocalLeftId}
            items={leftItems}
          />

          <Text>vs</Text>

          <SideSelector
            position="right"
            selectedId={localRightId}
            setSelectedId={setLocalRightId}
            items={rightItems}
          />
        </Group>
      </ModalShell>
    )
  },
)

export const showSelectSlenes = (props: SelectSlenesProps) => {
  NiceModal.show(SelectSlenesModal, props)
}
